import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { Business as BusinessIcon } from '@mui/icons-material';

interface ContactData {
  ContactOf?: string;
  companies?: Array<{ Id?: string | number; Name?: string; id?: string | number; name?: string }>;
  [key: string]: unknown;
}

interface CustomerChip {
  id: string;
  name: string;
}

function tryParseJSON(value: unknown): Record<string, string> | null {
  if (typeof value !== 'string' || !value) return null;
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, string>;
    }
    return null;
  } catch {
    return null;
  }
}

function extractCustomers(contact: ContactData): CustomerChip[] {
  // Try v3 format first: ContactOf is a JSON string { [customerId]: customerName }
  const parsed = tryParseJSON(contact.ContactOf);
  if (parsed) {
    return Object.entries(parsed).map(([id, name]) => ({ id, name }));
  }

  // Try v3.2 format: companies array
  if (Array.isArray(contact.companies) && contact.companies.length > 0) {
    return contact.companies.map((c) => ({
      id: String(c.Id ?? c.id ?? ''),
      name: c.Name || c.name || 'Unknown',
    }));
  }

  return [];
}

interface LinkedCustomersSectionProps {
  contact: ContactData;
}

export default function LinkedCustomersSection({ contact }: LinkedCustomersSectionProps) {
  const customers = extractCustomers(contact);

  return (
    <Box sx={{ px: 2, py: 2 }}>
      <Typography
        variant="overline"
        sx={{
          fontWeight: 700,
          letterSpacing: '0.08em',
          color: 'text.secondary',
          display: 'block',
          mb: 1.5,
        }}
      >
        Linked Customers
      </Typography>

      {customers.length === 0 ? (
        <Typography
          variant="body2"
          sx={{ color: 'text.disabled', fontStyle: 'italic' }}
        >
          No linked customers
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {customers.map((customer) => (
            <Chip
              key={customer.id}
              icon={<BusinessIcon />}
              label={customer.name}
              variant="outlined"
              size="small"
              clickable
              sx={{
                fontWeight: 500,
                borderColor: 'primary.light',
                color: 'primary.dark',
                '& .MuiChip-icon': { color: 'primary.main' },
                '&:hover': {
                  bgcolor: 'primary.50',
                  borderColor: 'primary.main',
                },
                transition: 'all 0.15s ease',
              }}
              aria-label={`Linked customer: ${customer.name}`}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
